import { http } from '@services/http/client';
import type {
  ForwardingLog,
  ForwardingStatus,
  Paginated,
  SenderAggregate,
  SenderBlock,
} from '@app-types/index';

import { buildParams, mapPaginated, type RawPaginated } from './_shared';
import { ENDPOINTS } from './endpoints';

// ---------------------------------------------------------------------------
// Wire-format types (snake_case, as the backend serializers emit them).
// ---------------------------------------------------------------------------
interface RawForwardingLog {
  id: string;
  alias: { id: string; full_address: string; label: string };
  from_email: string;
  subject: string;
  status: ForwardingStatus;
  created_at: string;
}

interface RawSender {
  sender_email: string;
  message_count: number;
  last_received_at: string | null;
  is_blocked: boolean;
  alias_id: string;
  alias_address: string;
  block_id: string | null;
}

interface RawSenderBlock {
  id: string;
  alias: string;
  sender_email: string;
  reason: string | null;
  created_at: string;
}

const mapLog = (raw: RawForwardingLog): ForwardingLog => ({
  id: raw.id,
  alias: {
    id: raw.alias.id,
    fullAddress: raw.alias.full_address,
    label: raw.alias.label,
  },
  fromEmail: raw.from_email,
  subject: raw.subject,
  status: raw.status,
  createdAt: raw.created_at,
});

const mapSender = (raw: RawSender): SenderAggregate => ({
  senderEmail: raw.sender_email,
  messageCount: raw.message_count,
  lastReceivedAt: raw.last_received_at,
  isBlocked: raw.is_blocked,
  aliasId: raw.alias_id,
  aliasAddress: raw.alias_address,
  blockId: raw.block_id,
});

const mapSenderBlock = (raw: RawSenderBlock): SenderBlock => ({
  id: raw.id,
  alias: raw.alias,
  senderEmail: raw.sender_email,
  reason: raw.reason,
  createdAt: raw.created_at,
});

// ---------------------------------------------------------------------------
// Request param / body shapes (camelCase in — snake_case on the wire).
// ---------------------------------------------------------------------------
export interface ForwardingLogParams {
  status?: ForwardingStatus;
  alias?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  ordering?: 'created_at' | '-created_at';
  page?: number;
  pageSize?: number;
}

export interface SenderListParams {
  alias?: string;
  search?: string;
  blocked?: boolean;
  page?: number;
  pageSize?: number;
}

export interface SenderBlockListParams {
  alias?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface CreateSenderBlockInput {
  alias: string;
  senderEmail: string;
  reason?: string;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
export const forwardingApi = {
  logs: async (params: ForwardingLogParams = {}): Promise<Paginated<ForwardingLog>> => {
    const raw = await http.get<unknown, RawPaginated<RawForwardingLog>>(
      ENDPOINTS.forwarding.logs,
      {
        params: buildParams({
          status: params.status,
          alias: params.alias,
          date_from: params.dateFrom,
          date_to: params.dateTo,
          search: params.search,
          ordering: params.ordering,
          page: params.page,
          page_size: params.pageSize,
        }),
      },
    );
    return mapPaginated(raw, mapLog);
  },

  senders: async (params: SenderListParams = {}): Promise<Paginated<SenderAggregate>> => {
    const raw = await http.get<unknown, RawPaginated<RawSender>>(
      ENDPOINTS.forwarding.senders,
      {
        params: buildParams({
          alias: params.alias,
          search: params.search,
          blocked: params.blocked,
          page: params.page,
          page_size: params.pageSize,
        }),
      },
    );
    return mapPaginated(raw, mapSender);
  },

  senderBlocks: async (
    params: SenderBlockListParams = {},
  ): Promise<Paginated<SenderBlock>> => {
    const raw = await http.get<unknown, RawPaginated<RawSenderBlock>>(
      ENDPOINTS.forwarding.senderBlocks,
      {
        params: buildParams({
          alias: params.alias,
          search: params.search,
          page: params.page,
          page_size: params.pageSize,
        }),
      },
    );
    return mapPaginated(raw, mapSenderBlock);
  },

  // Idempotent: a duplicate (alias, sender) returns the existing block (200).
  createSenderBlock: async (input: CreateSenderBlockInput): Promise<SenderBlock> => {
    const body: Record<string, string> = {
      alias: input.alias,
      sender_email: input.senderEmail,
    };
    if (input.reason !== undefined) body.reason = input.reason;
    const raw = await http.post<unknown, RawSenderBlock>(
      ENDPOINTS.forwarding.senderBlocks,
      body,
    );
    return mapSenderBlock(raw);
  },

  deleteSenderBlock: async (id: string): Promise<void> => {
    await http.delete(ENDPOINTS.forwarding.senderBlockById(id));
  },
};
